
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanLoad, Route, Router, RouterStateSnapshot, UrlSegment } from '@angular/router';
import { getRolesForUrl, Role } from '../auth/roles-url';
import { StorageService } from '../../services/storage.service';

@Injectable()
export class AuthorizatedGuard implements CanActivate, CanLoad {
  constructor(
    private router: Router,
    private storageService: StorageService
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    // Bloquea usuarios no autenticados.
    if (!this.storageService.isAuthenticated()) {
      this.router.navigate(['/login']);
      return false;
    }

    // Prefiere roles explicitos en la ruta; si no, usa el mapa central por URL.
    const requiredRoles = this.getRequiredRoles(state.url, route.data?.roles as Role[] | Role | undefined);
    if (!requiredRoles || requiredRoles.length === 0) {
      // Ruta pública una vez autenticado.
      return true;
    }

    // Permite si el usuario tiene al menos un rol requerido.
    if (this.storageService.hasAnyRole(requiredRoles)) {
      return true;
    }

    // Autenticado pero sin permisos.
    this.router.navigate(['/404']);
    return false;
  }

  canLoad(route: Route, segments: UrlSegment[]): boolean {
    // Evita cargar módulos lazy si no está autenticado.
    if (!this.storageService.isAuthenticated()) {
      this.router.navigate(['/login']);
      return false;
    }

    const url = '/' + segments.map((segment) => segment.path).join('/');
    // Prefiere roles explícitos en la ruta; si no, usa el mapa central por URL.
    const requiredRoles = this.getRequiredRoles(url, route.data?.roles as Role[] | Role | undefined);
    if (!requiredRoles || requiredRoles.length === 0) {
      // Módulo público una vez autenticado.
      return true;
    }

    // Permite si el usuario tiene al menos un rol requerido.
    if (this.storageService.hasAnyRole(requiredRoles)) {
      return true;
    }

    // Autenticado pero sin permisos.
    this.router.navigate(['/404']);
    return false;
  }

  private getRequiredRoles(url: string, routeRoles?: Role[] | Role): Role[] | null {
    // Los roles en la ruta tienen prioridad sobre el mapa central por URL.
    const normalizedRouteRoles = this.normalizeRoles(routeRoles);
    if (normalizedRouteRoles) {
      return normalizedRouteRoles;
    }

    return getRolesForUrl(url);
  }

  private normalizeRoles(roles?: Role[] | Role): Role[] | null {
    if (!roles) {
      return null;
    }

    return Array.isArray(roles) ? roles : [roles];
  }
}
